import React, { useState } from 'react';
import { ShieldCheck, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { API_URL } from '../../config';

// Formulaire de verification email (code a 6 chiffres) - reutilise a l'inscription
// (Signup.jsx, juste apres la creation du compte) et a la connexion (Login.jsx, quand
// le backend renvoie error_code 'email_not_verified' pour un compte pas encore active).
export default function EmailVerifyForm({ userId, email, onVerified, onCancel }) {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [resendMsg, setResendMsg] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6 || verifying) return;
    setError('');
    setVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, code: code.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        onVerified();
      } else {
        setError(data.message || 'Code incorrect.');
      }
    } catch (err) {
      setError('Impossible de contacter le serveur. Réessayez.');
    }
    setVerifying(false);
  };

  const handleResend = async () => {
    if (resending) return;
    setError('');
    setResendMsg('');
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      setResendMsg(data.success ? 'Nouveau code envoyé !' : (data.message || 'Erreur lors de l\'envoi.'));
    } catch (err) {
      setResendMsg('Impossible de contacter le serveur.');
    }
    setResending(false);
  };

  return (
    <form onSubmit={handleVerify} className="space-y-5">
      <div className="text-center">
        <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 items-center justify-center mb-3 shadow-lg">
          <ShieldCheck className="h-7 w-7 text-white" />
        </div>
        <h2 className="text-xl font-bold text-white mb-1">Vérifiez votre email</h2>
        <p className="text-blue-200/70 text-sm">
          Un code à 6 chiffres a été envoyé à <span className="text-blue-300 font-medium">{email}</span>
        </p>
      </div>

      <input
        type="text"
        inputMode="numeric"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-4 text-white text-center text-2xl tracking-[0.5em] font-bold placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
        placeholder="000000"
        autoFocus
      />

      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={code.trim().length !== 6 || verifying}
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3.5 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 shadow-xl"
      >
        {verifying ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Vérifier le code'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="inline-flex items-center gap-1.5 text-blue-300 hover:text-blue-200 text-sm font-medium transition-colors disabled:opacity-50"
        >
          {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Renvoyer le code
        </button>
        {resendMsg && <p className="text-blue-200/60 text-xs mt-2">{resendMsg}</p>}
      </div>

      {onCancel && (
        <div className="text-center">
          <button type="button" onClick={onCancel} className="text-blue-200/50 hover:text-blue-200 text-xs transition-colors">
            Retour
          </button>
        </div>
      )}
    </form>
  );
}
